import { alphabetRegex, punctuationRegex } from './calculateWords';

export interface ArticleEvaluationResult {
  gradeCounts: Record<string, number>;
  cumulativeCounts: Record<string, number>;
  missingCharacters: string[];
  missingCount: number;
  recommendedGrade: string;
  totalCharacters: number;
  uniqueCharacters: number;
}

const parseLevel = (level: string) => {
  const [gradePart, chapterPart] = level.split('.');
  return {
    grade: Number.parseInt(gradePart, 10),
    chapter: chapterPart ? Number.parseInt(chapterPart, 10) : 0,
  };
};

const getSortedGlossaryLevels = (levels: string[]) => {
  return levels.sort((a, b) => {
    const levelA = parseLevel(a);
    const levelB = parseLevel(b);
    if (levelA.grade !== levelB.grade) {
      return levelA.grade - levelB.grade;
    }
    return levelA.chapter - levelB.chapter;
  });
};

export const evaluateArticle = (
  article: string,
  glossary: string,
  _targetLevel?: string,
): ArticleEvaluationResult => {
  const supportGlossaryList = ['GRCS', 'HSK'];
  const cleanedArticle = article
    .split(punctuationRegex)
    .join('')
    .split(alphabetRegex)
    .join('')
    .split('\n')
    .join('');
  const characters = cleanedArticle.split('').filter((char) => char.trim());
  const uniqueCharacters = new Set<string>(characters);

  // contains the glossary as <level, character[]>
  let glossaryList: Record<string, string[]> = {};

  if (article.length === 0 || !supportGlossaryList.includes(glossary)) {
    console.error('Article is empty or glossary is not in supported list');
    return {
      gradeCounts: {},
      cumulativeCounts: {},
      missingCharacters: [],
      missingCount: 0,
      recommendedGrade: '',
      totalCharacters: 0,
      uniqueCharacters: 0,
    };
  }
  if (glossary === 'GRCS') {
    const grcs: Record<
      string,
      string
    > = require('@grcs/dictionary/hanzi_list.json');
    // Convert Record<string, string> to Record<string, string[]>
    const grcsList: Record<string, string[]> = Object.fromEntries(
      Object.entries(grcs).map(([key, value]) => [key, value.split('')]),
    );
    glossaryList = grcsList;
  } else if (glossary === 'HSK') {
    const hsk: Record<
      string,
      string
    > = require('@grcs/dictionary/hsk_list.json');
    const hskList: Record<string, string[]> = Object.fromEntries(
      Object.entries(hsk).map(([key, value]) => [key, value.split('')]),
    );
    glossaryList = hskList;
  }

  const gradeCounts: Record<string, number> = {};
  const cumulativeCounts: Record<string, number> = {};
  const characterToLevel = new Map<string, string>();
  const levels = getSortedGlossaryLevels(Object.keys(glossaryList));

  levels.forEach((level) => {
    glossaryList[level].forEach((char) => {
      if (!characterToLevel.has(char)) {
        characterToLevel.set(char, level);
      }
    });
  });

  const missingCharacterSet = new Set<string>();
  characters.forEach((char) => {
    const level = characterToLevel.get(char);
    if (level) {
      gradeCounts[level] = (gradeCounts[level] || 0) + 1;
    } else {
      missingCharacterSet.add(char);
    }
  });

  let cumulative = 0;
  levels.forEach((level) => {
    cumulative += gradeCounts[level] || 0;
    cumulativeCounts[level] = cumulative;
  });

  const totalCharacters = characters.length;
  const missingCount = totalCharacters - cumulative;
  if (missingCount > 0) {
    cumulativeCounts.unknown = totalCharacters;
  }

  let recommendedGrade = levels[levels.length - 1] || '';
  const coverageTarget = totalCharacters > 0 ? 0.9 : 0;
  for (const level of levels) {
    const coverage =
      totalCharacters > 0
        ? (cumulativeCounts[level] || 0) / totalCharacters
        : 0;
    if (coverage >= coverageTarget) {
      recommendedGrade = level;
      break;
    }
  }

  return {
    gradeCounts,
    cumulativeCounts,
    missingCharacters: Array.from(missingCharacterSet),
    missingCount,
    recommendedGrade,
    totalCharacters,
    uniqueCharacters: uniqueCharacters.size,
  };
};
